import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { adminApi } from '../../services/api';
import { fmtUSD } from '../../utils/formatters';

const SwapManagement = () => {
  const dispatch = useDispatch();
  const [rates, setRates] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('rates');
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLong, setEditLong] = useState('');
  const [editShort, setEditShort] = useState('');
  const [editTriple, setEditTriple] = useState(true);
  const [editActive, setEditActive] = useState(false);
  const [saving, setSaving] = useState({});
  const [toggling, setToggling] = useState({});

  useEffect(() => { fetchRates(); fetchHistory(); }, []);

  const fetchRates = async () => {
    try { const res = await adminApi.get('/admin/swap/rates'); setRates(res.data?.rates || []); } catch (e) {}
    finally { setIsLoading(false); }
  };

  const fetchHistory = async () => {
    try { const res = await adminApi.get('/admin/swap/history'); setHistory(res.data?.history || []); } catch (e) {}
  };

  const handleToggleActive = async (instrumentId, currentActive) => {
    setToggling({ ...toggling, [instrumentId]: true });
    try {
      await adminApi.put(`/admin/swap/rates/${instrumentId}`, {
        swapLongPerLot: 0, swapShortPerLot: 0, tripleWednesday: true, isActive: !currentActive
      });
      setRates(prev => prev.map(r => r.instrumentId === instrumentId ? { ...r, isActive: !currentActive } : r));
      dispatch(showToast({ message: `Swap ${!currentActive ? 'activated' : 'deactivated'} for ${instrumentId}`, type: 'success' }));
    } catch (e) { dispatch(showToast({ message: 'Failed to toggle', type: 'error' })); }
    finally { setToggling({ ...toggling, [instrumentId]: false }); }
  };

  const handleSave = async (instrumentId) => {
    setSaving({ ...saving, [instrumentId]: true });
    try {
      await adminApi.put(`/admin/swap/rates/${instrumentId}`, {
        swapLongPerLot: Number(editLong) || 0,
        swapShortPerLot: Number(editShort) || 0,
        tripleWednesday: editTriple,
        isActive: editActive
      });
      dispatch(showToast({ message: 'Swap rate updated', type: 'success' }));
      setEditingId(null);
      fetchRates();
    } catch (e) { dispatch(showToast({ message: 'Failed to save', type: 'error' })); }
    finally { setSaving({ ...saving, [instrumentId]: false }); }
  };

  const getCategoryLabel = (cat) => {
    const labels = { forex: 'FOREX', crypto: 'CRYPTO', metal: 'METAL', stock: 'STOCK', index: 'INDEX', commodity: 'COMMODITY' };
    return labels[cat] || cat?.toUpperCase();
  };

  const getCategoryColor = (cat) => {
    const colors = { forex: '#059669', crypto: '#d97706', metal: '#d97706', stock: '#2563eb', index: '#7c3aed', commodity: '#0d9488' };
    return colors[cat] || '#64748b';
  };

  const getCategoryBg = (cat) => {
    const bgs = { forex: '#ecfdf5', crypto: '#fef9c3', metal: '#fef9c3', stock: '#eff6ff', index: '#f5f3ff', commodity: '#f0fdfa' };
    return bgs[cat] || '#f8fafc';
  };

  const groupedRates = {};
  rates.forEach(r => {
    const cat = r.category || 'other';
    if (!groupedRates[cat]) groupedRates[cat] = [];
    groupedRates[cat].push(r);
  });

  if (isLoading) return (
    <div className="flex justify-center py-20">
      <div className="w-10 h-10 border-[3px] border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b-2 border-[#e2e8f0] bg-[#f8fafc]">
        <div>
          <h2 className="font-syne text-xl font-extrabold text-[#0b1e33] uppercase tracking-tight">SWAP MANAGEMENT</h2>
          <p className="text-[11px] text-[#475569] font-mono font-extrabold mt-1 uppercase tracking-wider">Configure swap rates per instrument · Toggle active to enable overnight swap</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('rates')} 
            className={`px-5 py-2.5 rounded-xl text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all border-2 ${
              activeTab === 'rates' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-md' 
                : 'bg-white text-[#64748b] border-[#e2e8f0] hover:text-[#1e3a5f] hover:border-[#1e3a5f]'
            }`}>
            <i className="fas fa-sliders mr-1.5" /> RATES
          </button>
          <button onClick={() => setActiveTab('history')} 
            className={`px-5 py-2.5 rounded-xl text-[10px] font-syne font-extrabold uppercase tracking-wider transition-all border-2 ${
              activeTab === 'history' 
                ? 'bg-[#1e3a5f] text-white border-[#1e3a5f] shadow-md' 
                : 'bg-white text-[#64748b] border-[#e2e8f0] hover:text-[#1e3a5f] hover:border-[#1e3a5f]'
            }`}>
            <i className="fas fa-clock-rotate-left mr-1.5" /> HISTORY
          </button>
        </div>
      </div>

      {/* Rates Tab */}
      {activeTab === 'rates' && (
        <div className="p-5 space-y-5">
          {Object.entries(groupedRates).map(([cat, instruments]) => (
            <div key={cat} className="rounded-xl border-2 border-[#e2e8f0] overflow-hidden shadow-sm">
              {/* Category Header */}
              <div className="px-5 py-3 flex items-center gap-3 border-b-2 border-[#e2e8f0]" 
                style={{ background: getCategoryBg(cat) }}>
                <div className="w-3 h-3 rounded-full shadow-sm" style={{ background: getCategoryColor(cat) }} />
                <span className="text-xs font-extrabold text-[#0f172a] uppercase tracking-wider">{getCategoryLabel(cat)}</span>
                <span className="text-[10px] text-[#64748b] font-mono font-bold ml-2">{instruments.length} INSTRUMENTS</span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                      <th className="text-left px-5 py-3.5">INSTRUMENT</th>
                      <th className="text-center px-5 py-3.5">STATUS</th>
                      <th className="text-center px-5 py-3.5">LONG/LOT</th>
                      <th className="text-center px-5 py-3.5">SHORT/LOT</th>
                      <th className="text-center px-5 py-3.5">3X WED</th>
                      <th className="text-center px-5 py-3.5">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instruments.map(r => (
                      <tr key={r.instrumentId} className={`border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all ${r.isActive ? '' : 'opacity-40'}`}>
                        {/* Instrument */}
                        <td className="px-5 py-3.5">
                          <div className="font-syne text-xs font-extrabold text-[#0f172a]">{r.instrumentId}</div>
                          <div className="text-[10px] text-[#64748b] font-mono font-bold">{r.name}</div>
                        </td>

                        {/* Status Toggle */}
                        <td className="px-5 py-3.5 text-center">
                          <button
                            onClick={() => handleToggleActive(r.instrumentId, r.isActive)}
                            disabled={toggling[r.instrumentId]}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono font-extrabold uppercase tracking-wider transition-all border-2 ${
                              r.isActive
                                ? 'bg-[#059669] text-white border-[#047857] hover:bg-[#dc2626] hover:border-[#b91c1c]'
                                : 'bg-[#f1f5f9] text-[#94a3b8] border-[#e2e8f0] hover:bg-[#059669] hover:text-white hover:border-[#047857]'
                            }`}
                          >
                            {toggling[r.instrumentId] ? (
                              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <><span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? 'bg-white' : 'bg-[#cbd5e1]'}`} />{r.isActive ? 'ACTIVE' : 'INACTIVE'}</>
                            )}
                          </button>
                        </td>

                        {/* Long/Lot */}
                        <td className="px-5 py-3.5 text-center">
                          {editingId === r.instrumentId ? (
                            <input type="number" value={editLong} onChange={e => setEditLong(e.target.value)} placeholder="0" 
                              className="w-28 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-center text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#059669] transition-all" />
                          ) : (
                            <span className={`font-mono text-xs font-extrabold ${Number(r.swapLongPerLot) > 0 ? 'text-[#059669]' : 'text-[#94a3b8]'}`}>{fmtUSD(r.swapLongPerLot)}</span>
                          )}
                        </td>

                        {/* Short/Lot */}
                        <td className="px-5 py-3.5 text-center">
                          {editingId === r.instrumentId ? (
                            <input type="number" value={editShort} onChange={e => setEditShort(e.target.value)} placeholder="0" 
                              className="w-28 bg-white border-2 border-[#cbd5e1] rounded-lg px-3 py-2 text-center text-xs text-[#0f172a] font-extrabold outline-none focus:border-[#dc2626] transition-all" />
                          ) : (
                            <span className={`font-mono text-xs font-extrabold ${Number(r.swapShortPerLot) > 0 ? 'text-[#dc2626]' : 'text-[#94a3b8]'}`}>{fmtUSD(r.swapShortPerLot)}</span>
                          )}
                        </td>

                        {/* Triple Wednesday */}
                        <td className="px-5 py-3.5 text-center">
                          {editingId === r.instrumentId ? (
                            <input type="checkbox" checked={editTriple} onChange={e => setEditTriple(e.target.checked)} className="accent-[#1e3a5f] w-5 h-5 cursor-pointer" />
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-extrabold border-2 ${
                              r.tripleWednesday ? 'bg-[#059669] text-white border-[#047857]' : 'bg-[#f1f5f9] text-[#94a3b8] border-[#e2e8f0]'
                            }`}>{r.tripleWednesday ? 'YES' : 'NO'}</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5 text-center">
                          {editingId === r.instrumentId ? (
                            <div className="flex gap-2 justify-center">
                              <button onClick={() => handleSave(r.instrumentId)} disabled={saving[r.instrumentId]} 
                                className="px-4 py-2 rounded-lg bg-[#059669] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#047857] transition-all border-b-[2px] border-[#047857]">
                                {saving[r.instrumentId] ? '...' : 'SAVE'}
                              </button>
                              <button onClick={() => setEditingId(null)} 
                                className="px-4 py-2 rounded-lg bg-white border-2 border-[#e2e8f0] text-[#64748b] text-[10px] font-mono font-extrabold uppercase tracking-wider hover:text-[#0f172a] hover:border-[#cbd5e1] transition-all">
                                CANCEL
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingId(r.instrumentId); setEditLong(String(r.swapLongPerLot)); setEditShort(String(r.swapShortPerLot)); setEditTriple(r.tripleWednesday); setEditActive(r.isActive); }}
                              className="px-4 py-2 rounded-lg bg-[#1e3a5f] text-white text-[10px] font-mono font-extrabold uppercase tracking-wider hover:bg-[#0b1e33] transition-all border-b-[2px] border-[#0b1e33]"
                            >
                              <i className="fas fa-pen mr-1" /> EDIT
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#f1f5f9] border-b-2 border-[#e2e8f0] text-[9px] text-[#475569] font-mono font-extrabold uppercase tracking-wider">
                <th className="text-left px-5 py-3.5">DATE</th>
                <th className="text-left px-5 py-3.5">USER</th>
                <th className="text-center px-5 py-3.5">INSTRUMENT</th>
                <th className="text-center px-5 py-3.5">AMOUNT</th>
                <th className="text-center px-5 py-3.5">NIGHTS</th>
                <th className="text-center px-5 py-3.5">RATE/LOT</th>
                <th className="text-left px-5 py-3.5">APPLIED BY</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-16">
                    <div className="w-16 h-16 rounded-2xl bg-[#f1f5f9] flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-rotate text-[#94a3b8] text-2xl" />
                    </div>
                    <p className="text-[#475569] font-mono font-extrabold text-xs uppercase tracking-wider">No swap history</p>
                  </td>
                </tr>
              ) : (
                history.map(h => (
                  <tr key={h.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc] transition-all">
                    <td className="px-5 py-3.5 text-[11px] text-[#475569] font-mono font-bold">
                      {new Date(h.createdAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="text-[12px] text-[#0f172a] font-extrabold">{h.firstName} {h.lastName}</div>
                      <div className="text-[10px] text-[#64748b] font-mono font-bold">{h.email}</div>
                    </td>
                    <td className="px-5 py-3.5 text-center font-syne text-xs font-extrabold text-[#0f172a]">{h.instrumentId}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-xs font-extrabold text-[#dc2626]">-{fmtUSD(h.amount)}</td>
                    <td className="px-5 py-3.5 text-center text-[11px] text-[#475569] font-mono font-bold">{h.nights}</td>
                    <td className="px-5 py-3.5 text-center text-[11px] text-[#475569] font-mono font-bold">{fmtUSD(h.rate_per_lot)}</td>
                    <td className="px-5 py-3.5 text-[11px] text-[#1e3a5f] font-extrabold">{h.adminFirstName} {h.adminLastName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SwapManagement;